import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabImport = () => {
  const { csvFileRef, csvTemplate, customRssUrl, downloadCsvTemplate, handleImportCsv, handleImportRss, importResult, importingCsv, importingRss, jobs, rssFeeds, setCustomRssUrl, setImportResult } = useAdminCtx();
  // Indeed shut down public RSS feeds (403 Forbidden). Adzuna + Jooble remain the active daily aggregators, so the RSS import UI is hidden.
  const showRssImport = false;

  const [aggStatus, setAggStatus] = useState(null);
  const [aggLoading, setAggLoading] = useState('');

  const fetchAggStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/import/aggregator-status`);
      setAggStatus(res.data);
    } catch (e) {
      console.error('aggregator status error', e);
    }
  };

  useEffect(() => { fetchAggStatus(); }, []);

  const runAggImport = async (source) => {
    setAggLoading(source);
    try {
      const res = await axios.post(`${API_URL}/admin/import/${source}`, { max_pages: 1 });
      toast.success(`${source[0].toUpperCase() + source.slice(1)}: ${res.data.jobs_added || 0} added, ${res.data.jobs_skipped || 0} skipped`);
      fetchAggStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${source} import failed`);
    } finally {
      setAggLoading('');
    }
  };

  const AGG_SOURCES = [
    { key: 'adzuna', name: 'Adzuna', color: 'bg-indigo-100 text-indigo-600', url: 'developer.adzuna.com', configKey: 'adzuna_configured' },
    { key: 'jooble', name: 'Jooble', color: 'bg-teal-100 text-teal-600', url: 'jooble.org/api/about', configKey: 'jooble_configured' },
    { key: 'usajobs', name: 'USAJobs (Federal)', color: 'bg-blue-100 text-blue-600', url: 'developer.usajobs.gov', configKey: 'usajobs_configured' },
    { key: 'careeronestop', name: 'CareerOneStop', color: 'bg-orange-100 text-orange-600', url: 'careeronestop.org/Developers', configKey: 'careeronestop_configured' },
  ];

  const runForensicFederal = async () => {
    setAggLoading('forensic');
    try {
      const res = await axios.post(`${API_URL}/admin/import/forensic-federal`, {});
      toast.success(`Forensic (Federal): ${res.data.jobs_added || 0} added, ${res.data.jobs_skipped || 0} skipped`);
      fetchAggStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forensic import failed');
    } finally {
      setAggLoading('');
    }
  };

  return (
    <>
            {/* Aggregator APIs */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6" data-testid="aggregator-import-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Job Aggregator APIs</h3>
                  <p className="text-sm text-gray-500">Import social work jobs from connected job-data sources. Auto-runs daily; trigger manually below.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {AGG_SOURCES.map((s) => {
                  const configured = aggStatus?.[s.configKey];
                  const count = aggStatus?.active_jobs_by_source?.[s.key] ?? 0;
                  const cosSetup = s.key === 'careeronestop' && aggStatus?.careeronestop_status === 'setup_required';
                  return (
                    <div key={s.key} className={`border rounded-lg p-4 ${cosSetup ? 'border-amber-300 bg-amber-50' : 'border-gray-100'}`} data-testid={`agg-card-${s.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{s.name}</span>
                        {cosSetup ? (
                          <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid="cos-setup-badge"><AlertTriangle className="w-3 h-3 mr-1" />Setup Required</Badge>
                        ) : configured ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Connected</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-500 text-xs">Not configured</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">{count} active jobs from this source</p>
                      {cosSetup ? (
                        <p className="text-xs text-amber-800" data-testid="cos-setup-message">{aggStatus?.careeronestop_message}</p>
                      ) : configured ? (
                        <Button
                          size="sm"
                          onClick={() => runAggImport(s.key)}
                          disabled={aggLoading === s.key}
                          className="w-full bg-primary hover:bg-primary-hover text-white"
                          data-testid={`import-${s.key}-btn`}
                        >
                          {aggLoading === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Import Now'}
                        </Button>
                      ) : (
                        <p className="text-xs text-gray-400">Add API key in backend/.env — get free at {s.url}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-100 p-3 bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Forensic, Public Safety & Criminal Justice</p>
                  <p className="text-xs text-gray-500">Import federal forensic/police/court/corrections/victim-services social-work jobs (via USAJobs).</p>
                </div>
                <Button size="sm" onClick={runForensicFederal} disabled={aggLoading === 'forensic'} className="bg-primary hover:bg-primary-hover text-white" data-testid="import-forensic-federal-btn">
                  {aggLoading === 'forensic' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Import Forensic Jobs'}
                </Button>
              </div>
            </div>

            <div className="grid gap-6">
              {/* RSS Import — hidden (Indeed RSS feeds discontinued / 403) */}
              {showRssImport && (
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Rss className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Import from RSS Feed</h3>
                    <p className="text-sm text-gray-500">Import jobs from external job boards</p>
                  </div>
                </div>

                {/* Preset RSS Feeds */}
                <div className="space-y-3 mb-6">
                  <Label>Preset Job Feeds</Label>
                  <div className="space-y-2">
                    {Object.entries(rssFeeds).map(([key, url]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-gray-500 truncate max-w-xs">{url}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleImportRss(url)}
                          disabled={importingRss}
                          className="bg-primary hover:bg-primary-hover text-white"
                          data-testid={`import-rss-${key}`}
                        >
                          {importingRss ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Import'}
                        </Button>
                      </div>
                    ))}
                    {Object.keys(rssFeeds).length === 0 && (
                      <p className="text-gray-500 text-sm">No preset feeds available</p>
                    )}
                  </div>
                </div>

                {/* Custom RSS URL */}
                <div className="border-t pt-6">
                  <Label htmlFor="custom_rss">Custom RSS Feed URL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="custom_rss"
                      value={customRssUrl}
                      onChange={(e) => setCustomRssUrl(e.target.value)}
                      placeholder="https://example.com/jobs.rss"
                      className="flex-1"
                      data-testid="custom-rss-input"
                    />
                    <Button
                      onClick={() => handleImportRss(customRssUrl)}
                      disabled={importingRss || !customRssUrl}
                      className="bg-primary hover:bg-primary-hover text-white"
                      data-testid="import-custom-rss-btn"
                    >
                      {importingRss ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              )}

              {/* CSV Import */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Import from CSV</h3>
                    <p className="text-sm text-gray-500">Bulk import jobs from spreadsheet</p>
                  </div>
                </div>

                {/* Template Download */}
                <div className="mb-6">
                  <Button
                    variant="outline"
                    onClick={downloadCsvTemplate}
                    disabled={!csvTemplate}
                    className="w-full"
                    data-testid="download-csv-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV Template
                  </Button>
                  {csvTemplate && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">Required Columns:</p>
                      <div className="flex flex-wrap gap-1">
                        {csvTemplate.columns.map((col) => (
                          <Badge key={col} variant="secondary" className="text-xs">
                            {col}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* File Upload */}
                <div className="border-t pt-6">
                  <Label htmlFor="csv_file">Upload CSV File</Label>
                  <div className="mt-2">
                    <input
                      ref={csvFileRef}
                      type="file"
                      id="csv_file"
                      accept=".csv"
                      onChange={handleImportCsv}
                      className="hidden"
                      data-testid="csv-file-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => csvFileRef.current?.click()}
                      disabled={importingCsv}
                      className="w-full h-24 border-dashed flex flex-col items-center justify-center gap-2"
                      data-testid="upload-csv-btn"
                    >
                      {importingCsv ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-gray-400" />
                          <span className="text-gray-600">Click to upload CSV</span>
                          <span className="text-xs text-gray-400">or drag and drop</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Import Notes */}
                {csvTemplate?.notes && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-700 mb-2">Import Notes:</p>
                    <ul className="text-xs text-blue-600 space-y-1">
                      {csvTemplate.notes.map((note, i) => (
                        <li key={i}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`mt-6 p-4 rounded-lg ${
                importResult.type === 'success' ? 'bg-emerald-50 border border-emerald-200' :
                importResult.type === 'partial' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {importResult.type === 'success' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  ) : importResult.type === 'partial' ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${
                      importResult.type === 'success' ? 'text-emerald-700' :
                      importResult.type === 'partial' ? 'text-yellow-700' :
                      'text-red-700'
                    }`}>
                      {importResult.message || `Imported ${importResult.count || 0} jobs`}
                    </p>
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-yellow-600 mb-1">Errors ({importResult.errors.length}):</p>
                        <ul className="text-xs text-yellow-600 space-y-0.5">
                          {importResult.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setImportResult(null)}
                    className="text-gray-400"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
    </>
  );
};

export default AdminTabImport;
